/**
 * The list of companies the loan sheet offers — the single source of truth for
 * "who can hold one of our vehicles?".
 *
 * Three sources feed it, in this order:
 *
 * 1. **שינוע** — always first, because picking it is how a vehicle comes home.
 * 2. The five companies the unit lends to by habit, from
 *    {@link EXTERNAL_COMPANIES} in `src/core/vehicle-location.ts`.
 * 3. Every other company the unit has actually used — a name typed into the
 *    sheet and remembered, or one already written on a vehicle. A company that
 *    arrived on a restored backup is therefore on the list without anyone
 *    having to add it again.
 *
 * The third group is deduplicated against the first two and sorted in Hebrew,
 * so a company appears exactly once however it got here. The list is a
 * convenience and never a constraint: the sheet still takes a name typed by
 * hand, exactly as it did before.
 */
import {
  EXTERNAL_COMPANIES,
  HOME_COMPANY,
  isHomeCompany,
  normalizeCompany,
} from './vehicle-location';

/** One row of the sheet. */
export interface CompanyOption {
  name: string;
  /** שינוע — picking it is a return to base, not a loan. */
  home: boolean;
  /** Not one of the five the unit lends to by habit; the unit added it. */
  custom: boolean;
}

/** The part of a vehicle the directory reads — any stored record satisfies it. */
export interface CompanyHolderProbe {
  assignedCompany?: string | null;
}

/** True when the name is one of the five standing options. */
export function isStandardCompany(raw: string | null | undefined): boolean {
  return EXTERNAL_COMPANIES.includes(normalizeCompany(raw));
}

/**
 * The name as it would be filed, or `null` when it is not a company the
 * directory has to remember — a blank, ourselves, or one of the five.
 */
export function rememberableCompany(raw: string | null | undefined): string | null {
  const name = normalizeCompany(raw);
  if (!name || isHomeCompany(name) || isStandardCompany(name)) return null;
  return name;
}

/**
 * Adds a name to the remembered list, keeping it deduplicated and in Hebrew
 * order. A name the list already holds — or one it has no business holding —
 * leaves it untouched, so the caller can write the result back unconditionally.
 */
export function rememberCompany(
  remembered: readonly string[],
  raw: string | null | undefined,
): string[] {
  const name = rememberableCompany(raw);
  const held = remembered.map(normalizeCompany).filter(Boolean);
  if (!name || held.includes(name)) return held;
  return [...held, name].sort((a, b) => a.localeCompare(b, 'he'));
}

/**
 * The sheet's rows: שינוע, the five, then every company the unit has used,
 * each exactly once.
 */
export function listCompanyOptions(
  vehicles: readonly CompanyHolderProbe[] = [],
  remembered: readonly string[] = [],
): CompanyOption[] {
  const options: CompanyOption[] = [
    { name: HOME_COMPANY, home: true, custom: false },
    ...EXTERNAL_COMPANIES.map((name) => ({ name, home: false, custom: false })),
  ];

  const known = new Set(options.map((option) => option.name));
  const extra = new Set<string>();
  for (const source of [...remembered, ...vehicles.map((v) => v.assignedCompany)]) {
    const name = rememberableCompany(source);
    if (name && !known.has(name)) extra.add(name);
  }

  return [
    ...options,
    ...[...extra]
      .sort((a, b) => a.localeCompare(b, 'he'))
      .map((name) => ({ name, home: false, custom: true })),
  ];
}

/** The company names alone, in the same order — for a `<datalist>` or a test. */
export function companyNames(
  vehicles: readonly CompanyHolderProbe[] = [],
  remembered: readonly string[] = [],
): string[] {
  return listCompanyOptions(vehicles, remembered).map((option) => option.name);
}
